import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { imgUrl, formatPrice } from "@/lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, subtotal } = useCart();
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    if (user && user.role) {
      navigate("/checkout");
    } else {
      openAuth({ mode: "login", reason: "Please sign in to place your order.", onSuccess: () => navigate("/checkout") });
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 z-[81] w-full max-w-md glass flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="h-16 md:h-20 px-6 flex items-center justify-between border-b border-white/10">
              <h3 className="font-display font-bold uppercase tracking-[0.15em] flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" /> Your Bag
              </h3>
              <button onClick={() => setOpen(false)} data-testid="cart-close" aria-label="Close cart">
                <X className="w-6 h-6" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-8" data-testid="cart-empty">
                <ShoppingBag className="w-12 h-12 text-neutral-700" strokeWidth={1} />
                <p className="text-neutral-400">Your bag is empty.</p>
                <button
                  onClick={() => {
                    setOpen(false);
                    navigate("/shop");
                  }}
                  className="font-mono-th text-xs uppercase tracking-[0.2em] border border-white/20 px-6 py-3 hover:bg-white/5"
                  data-testid="cart-empty-shop-btn"
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                  {items.map((it) => (
                    <div key={`${it.productId}-${it.size}`} className="flex gap-4" data-testid={`cart-item-${it.productId}`}>
                      <img src={imgUrl(it.image)} alt={it.name} className="w-20 h-24 object-cover bg-[#1A1D24]" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-display text-sm font-medium leading-tight line-clamp-2">{it.name}</h4>
                        <p className="font-mono-th text-[10px] text-neutral-500 mt-1">SIZE {it.size}</p>
                        <p className="font-display font-semibold text-sm mt-1">{formatPrice(it.price)}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center border border-white/15">
                            <button onClick={() => updateQty(it.productId, it.size, it.quantity - 1)} className="p-1.5 hover:bg-white/5" data-testid={`cart-dec-${it.productId}`}>
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-8 text-center font-mono-th text-xs">{it.quantity}</span>
                            <button onClick={() => updateQty(it.productId, it.size, it.quantity + 1)} className="p-1.5 hover:bg-white/5" data-testid={`cart-inc-${it.productId}`}>
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <button onClick={() => removeItem(it.productId, it.size)} className="text-neutral-500 hover:text-rose-400" data-testid={`cart-remove-${it.productId}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-mono-th text-xs uppercase tracking-[0.2em] text-neutral-400">Subtotal</span>
                    <span className="font-display font-bold text-lg" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
                  </div>
                  <button
                    onClick={goCheckout}
                    className="w-full bg-white text-black py-4 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold hover:bg-neutral-200 transition-colors"
                    data-testid="cart-checkout-btn"
                  >
                    Checkout
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
